import React from 'react';
import {configData} from "../config.js";
import { format } from 'date-fns';
import UseCaseDetails from "../components/useCase/UseCaseDetails.js";
import SelectedReferenceList from '../components/general/SelectedReferenceList.js';
import SimpleDateInput from '../components/general/SimpleDateInput.js';
import DataProductUsage from '../components/useCase/DataProductUsage.js';
import '../styles.scss';

import {fetchSigned, convertToDate,
        isRecentMessage, createTimedMessage, extractTimedMessage} from "../shared/Utilities.js"

import {
  OwcButton, OwcTypography, OwcExpandableGroup, OwcExpandable,
  OwcProgressSpinner, OwcAssistiveText,
  OwcSwitch, OwcInput, OwcIcon,
} from '@one/react';

/**
 * The interactive form for tracking the implementation of a use case
 *
 * @copyright Roche 2025
 * @author Nick Draper
 */
class ImplementationTrackingUseCase extends React.Component {
  UNSAVED_CHANGES_MESSAGE = "Unsaved changes, click Save Changes to save";
  /**
   * Constructor 
   * 
   * @param props The properties passed
   */
  constructor(props) {
    super(props);
    this.state = this.initialState();
    if (props.useCaseId !== null)
    {
      this.state.isLoading = true;
    }
  }




  /**
   * Runs once after construction after everything is initialised
   */
  componentDidMount() {
    this.loadUseCaseData(this.props.useCaseId, false);
  }

  /** Runs whenever the properties of the control are changed
   * @param prevProps The previous properties dictionary
   * @param prevState The previous state dictionary
   */
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.useCaseId !== this.props.useCaseId) {
      this.loadUseCaseData(this.props.useCaseId, false);
    }
  }

  /**
   * Sets the initial state
   * @returns the initial state dictionary
   */
  initialState() {
    return ({
      isLoading: false,
      useCase:{useCaseId:-1, name:"", contactEmail:null,
        description: null,
        dataDescription: null,
        createdBy: null,
        dateCreated: null,
        dateUpdated: null,
        implementationStatus: null,
        computationalGovernanceApplied: null,
        actualStartDate: null,
        valueUnlocked: null,
        valueUnlockedDescription: null,
      },
      implementationStatusList: ["Not yet active", "Active", "Abandoned"],
      submissionState: null,
      isUseCaseExpanded: true,
      isImplementationTrackingExpanded: true,
      displayDPUsage: null,
    });
  }

  /**
   * Resets the form by setting the state back to the inital state
   */
  handleCancelClick() {
    //reset the state
    this.loadUseCaseData(this.props.useCaseId, true);
    this.props.onUnsavedChangesChange(false);
  }

  /**
   * Validates the form and submits it to the API if valid
   * @returns nothing
   */
  handleSubmitClick() {
    const submissionData = {...this.state.useCase}

    if (submissionData.actualStartDate !== null) {
      // remove the time and time zone component
      submissionData.actualStartDate = format(submissionData.actualStartDate, 'yyyy-MM-dd');
    }

    this.setState({ submissionState: "Saving ..." });

    const submitForm = () => {
      // decide if it is an update or insert and setup appropriately
      const url = `${configData.USECASE_API_URL}implementation/${submissionData.useCaseId}/`;
      const method = 'POST';
      return fetchSigned(url, {
        method: method,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      })
    };

    submitForm().then((response) => {
      response.json().then((json) => {
        console.log(`API Response: ${JSON.stringify(json)}`);
        if (response.status === 201) // inserted successfully
        {
          // load the data back from the database to update all fields and ids
          this.loadUseCaseData(json["useCaseId"], false);
          this.props.onUnsavedChangesChange(false);
          this.setState({ submissionState: "Data Use Successfully Saved" });
        } else {
          if (json.errorText.startsWith("ERROR: duplicate key value violates unique constraint")) {
            this.setState({ submissionState: "Error: This data use has already been entered." });
          } else {
            this.setState({ submissionState: `Error saving data use ${json.errorText}` });
          }
        }
      });
    }).catch((error) => {
      this.setState({ submissionState: `Error saving data use ${error}` });
      console.error(`Error when saving data use: ${error}`);
    });

  };

  /**
   * Loads a useCase
   * @param {*} ev the event
   * @param {*} useCaseId  the selected agreeemntId
   */
  loadUseCaseData(useCaseId, clearSubmissionState = true) { 
    this.setState({isLoading:true});
    console.log("Selected use case id: " + useCaseId);

    if (useCaseId !== null) {
      fetchSigned(configData.USECASE_API_URL + useCaseId + "/")
        .then(res => res.json())
        .then(
          (result) => {
            const newState = {useCase: result,
              isLoading: false
            }; 
            console.log(result);
            if (clearSubmissionState) {
              newState.submissionState = null;
            }
            this.setState(newState, this.props.onUnsavedChangesChange(false));
          },
          // Note: it's important to handle errors here
          // instead of a catch() block so that we don't swallow
          // exceptions from actual bugs in components.
          (error) => { this.setState({error}) }
      );
    } else {
      this.setState(this.initialState(), this.props.onUnsavedChangesChange(false));
    };    
  }

  handleListSelectionChange(selectedList, stateName) {  
    const newState = {submissionState: this.UNSAVED_CHANGES_MESSAGE};
    newState[stateName] = selectedList;
    this.setState(newState, this.props.onUnsavedChangesChange(true));
    
  }


  renderImpTrackingSection() {
    let warningMessage = "";
    if (this.state.useCase.actualStartDate !== null)
    {
      const startDate = new Date(this.state.useCase.actualStartDate)
      const now = new Date()
      if ((this.state.useCase.implementationStatus==="Active"
        || this.state.useCase.implementationStatus==="Abandoned")
        && (startDate > now)) {
          warningMessage = `The start date should be in the past for ${this.state.useCase.implementationStatus} data uses.`
      } else if ((this.state.useCase.implementationStatus==="Not yet active"
        || this.state.useCase.implementationStatus===null)
        && (startDate < now)) {
          warningMessage = `The start date should be in the future for ${this.state.useCase.implementationStatus} data uses.`
      }
    }
    
    return (
    <OwcExpandable variant="standard" round
      expanded={this.state.isImplementationTrackingExpanded}
      onExpandedChange={(ev) => this.setState({ isImplementationTrackingExpanded: ev.detail })}>
      <span slot="title">
        <OwcTypography variant="title5">
          Implementation Tracking
        </OwcTypography>
      </span>
      <span slot="content">
        <table>
          <thead></thead>
          <tbody>
            <tr>
              <td><OwcTypography>Computational Governance Applied</OwcTypography></td>
              <td><div style={{display: "flex", flexDirection: "row", gap: "2em"}}>
                <OwcSwitch checked={this.state.useCase.computationalGovernanceApplied}
                  disabled={this.isUseCaseHidden()}
                  onValueChange={(ev) => {
                    if (ev.detail !== this.state.useCase.computationalGovernanceApplied) {
                      const newUseCase = { ...this.state.useCase };
                      newUseCase.computationalGovernanceApplied = ev.detail;
                      this.setState({
                        useCase: newUseCase,
                        submissionState: this.UNSAVED_CHANGES_MESSAGE
                      }, this.props.onUnsavedChangesChange(true));
                    }
                  } } />
                <OwcButton disabled={this.isUseCaseHidden()}
                    onClick={()=>{this.setState({displayDPUsage:createTimedMessage(this.state.useCase.useCaseId)});}}>
                  Show Recent Data Product Usage
                </OwcButton>
                </div>
              </td>
            </tr>
            <tr>
              <td><OwcTypography>Data use has been realized</OwcTypography></td>
              <td>
                <select style={{ width: "auto", position: "relative", zIndex: "2" }}
                    disabled={this.isUseCaseHidden()}
                    value={this.state.useCase.implementationStatus === null ? this.state.implementationStatusList[0] : this.state.useCase.implementationStatus}
                    onChange={(ev) => {
                      if (ev.target.value !== this.state.useCase.implementationStatus) {
                        const newUseCase = { ...this.state.useCase };
                        newUseCase.implementationStatus = ev.target.value;
                        this.setState({
                          useCase: newUseCase,
                          submissionState: this.UNSAVED_CHANGES_MESSAGE
                        }, this.props.onUnsavedChangesChange(true));
                      }
                    }}>
                  {this.state.implementationStatusList.map((item, index) => {
                    return (
                      <option key={"implementationStatus" + index}>{item}</option>
                    );
                  }
                  )}
                </select>
              </td>
            </tr>
            <tr>
              <td style={{verticalAlign:"top", paddingTop:"1em"}}><OwcTypography>Actual Start Date</OwcTypography></td>
              <td>
                <SimpleDateInput name="dateActual" label=""
                  disabled={this.isUseCaseHidden()}
                  value={this.state.useCase.actualStartDate}
                  exampleDate="current"
                  displayWarning = {warningMessage!==""}
                  onValueChange={(ev) => {
                    const fieldName = "actualStartDate";
                    const result = { ...this.state.useCase };
                    if ((ev === null) && (this.state.useCase[fieldName] !== null)) {
                      result[fieldName] = null;
                      this.setState({
                        useCase: result,
                        submissionState: this.UNSAVED_CHANGES_MESSAGE
                      }, this.props.onUnsavedChangesChange(true));
                    } else {
                      const newDateParsed = convertToDate(ev);
                      if (newDateParsed !== result[fieldName]) {
                        result[fieldName] = newDateParsed;
                        //save the change
                        this.setState({
                          useCase: result,
                          submissionState: this.UNSAVED_CHANGES_MESSAGE
                        }, this.props.onUnsavedChangesChange(true));
                      }
                    }
                  } } />
                  <OwcAssistiveText style={{color:"orange"}}>
                    {warningMessage}
                  </OwcAssistiveText>
              </td>
            </tr>
            <tr>
              <td><OwcTypography>Value Unlocked</OwcTypography></td>
              <td colSpan="3">
                <OwcInput key="valueUnlocked" type="number"
                    disableValidation
                    disabled={this.isUseCaseHidden()}
                    value={String(this.state.useCase.valueUnlocked)}
                    onValueChange={(ev) => {
                        if (ev.detail !== this.state.useCase.valueUnlocked) {
                          const newUseCase = { ...this.state.useCase };
                          console.log(`New value: -${ev.detail}-`);
                          if (ev.detail === "") {
                            newUseCase.valueUnlocked = null;
                          } else {
                            newUseCase.valueUnlocked = Number(ev.detail);
                          }
                          
                          this.setState({
                            useCase: newUseCase,
                            submissionState: this.UNSAVED_CHANGES_MESSAGE
                          }, this.props.onUnsavedChangesChange(true));
                        }
                      }
                    }>
                  <OwcIcon slot="prefix">
                    <svg width="20px" height="20px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                      <path d="M541.7 768v-45.3c46.3-2.4 81.5-15 108.7-37.8 27.2-22.8 40.8-53.1 40.8-88.2 0-37.8-11-65.7-35.3-83.4-24.6-20.1-59.8-35.4-111.6-45.3h-2.6V351.8c35.3 5.1 65.3 15 95.1 35.4l43.6-55.5c-43.6-27.9-89.9-42.9-138.8-45.3V256h-40.8v30.3c-40.8 2.4-76.3 15-103.5 37.8-27.2 22.8-40.8 53.1-40.8 88.2s11 63 35.3 80.7c21.7 17.7 59.8 32.7 108.7 42.9v118.5c-38.2-5.1-76.3-22.8-114.2-53.1l-48.9 53.1c48.9 40.5 103.5 63 163.3 68.1V768h41zm2.6-219.6c27.2 7.5 43.6 15 54.4 22.8 8.1 10.2 13.6 20.1 13.6 35.4s-5.5 25.2-19.1 35.4c-13.6 10.2-30.1 15-48.9 17.7V548.4zM449.2 440c-8.1-7.5-13.6-20.1-13.6-32.7 0-15 5.5-25.2 16.2-35.4 13.6-10.2 27.2-15 48.9-17.7v108.6c-27.2-7.8-43.4-15.3-51.5-22.8z" />
                    </svg>
                  </OwcIcon>
                  <OwcAssistiveText slot="assistive-text">Approximate value in USD</OwcAssistiveText>
                </OwcInput>
              </td>
            </tr>
            <tr>
              <td colSpan="3">
                <OwcInput key={"valueUnlockedDescription"} style={{ width: "100%" }} cols="100"
                  label="Additional Information"
                  value={this.state.useCase.valueUnlockedDescription}
                  disabled={this.isUseCaseHidden()}
                  onValueChange={(ev) => {
                      if (ev.detail !== this.state.useCase.valueUnlockedDescription) {
                        const newUseCase = { ...this.state.useCase };
                        newUseCase.valueUnlockedDescription = String(ev.detail);
                        this.setState({
                          useCase: newUseCase,
                          submissionState: this.UNSAVED_CHANGES_MESSAGE
                        }, this.props.onUnsavedChangesChange(true));
                      }
                    }
                  }
                  maxlength="1000"
                  rows={this.state.valueUnlockedDescription == null ? 4 : Math.max(4, this.state.valueUnlockedDescription.split(/\r\n|\r|\n/).length)}
                  type="textarea"
                >
                  <OwcAssistiveText slot="assistive-text">
                    Optional additional information related to the value unlocked
                  </OwcAssistiveText>
                </OwcInput>
              </td>
            </tr>
          </tbody>
        </table>
      </span>
    </OwcExpandable>
    );
  }

  isUseCaseHidden() {
    return this.state.useCase.hiddenFromDataProducts===true;
  }

  /**
   * Renders the controls
   * @returns The JSX of the controls
   */
  render() {
    let messageColour = "black";
    if (this.state.submissionState !== null && this.state.submissionState.startsWith("Err")) {
      messageColour = "red";
    }

    let showDPUsage = null;
    if (isRecentMessage(this.state.displayDPUsage))
    {
      showDPUsage = extractTimedMessage(this.state.displayDPUsage);
    }

    const submissionState = this.state.submissionState;

    if (this.props.useCaseId === null) {
      return (<OwcTypography variant="title6" style={{marginLeft:"0.5em"}}>Save a data use in "Register" first</OwcTypography>);
    } else {
      if (this.state.isLoading){
        return (
          <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
            <OwcProgressSpinner style={{marginTop:'30px'}}/>
          </div>);
      } else {
        return (
          <div>
            <DataProductUsage
              key="DataProductUsageDisplay"
              useCaseId={showDPUsage}
              showNow={this.state.displayDPUsage} />  
            <OwcExpandableGroup multiple>
            <OwcExpandable variant="standard" round
                expanded={this.state.isUseCaseExpanded}
                onExpandedChange={(ev) => this.setState({ isUseCaseExpanded: ev.detail })} >
              <span slot="title">
                <div style={{display:'flex', flexDirection:'row', alignItems: 'center'}}>
                  <OwcTypography variant="title5">
                    {!this.state.isUseCaseExpanded
                      ? `Data Use${this.state.useCase.isGroup===true?" Group": ""} ${this.state.useCase.useCaseId} - ${this.state.useCase.name}`
                      : `Data Use${this.state.useCase.isGroup===true?" Group": ""} ${this.state.useCase.useCaseId}`}
                  </OwcTypography>
                  {this.state.useCase.hiddenFromDataProducts
                    ? <OwcIcon key="useCaseHiddenIcon" id="useCaseHiddenIcon" name="element_hide"
                        style={{fontSize:"1.5em", paddingLeft:"0.5em"}} />
                    : ""
                  }
                </div>
              </span>
              <span slot="content">
                <UseCaseDetails useCase={this.state.useCase}/>
                <SelectedReferenceList selectedRefDataList={this.state.useCase.purposeList} title="Purpose Classification:"/>              
              </span>
            </OwcExpandable>
            {this.renderImpTrackingSection()}
            </OwcExpandableGroup>
            <div style={{ display:"flex", flexDirection:"column" }}>
              <table width="100%">
                <tbody>
                  <tr>
                    <td style={{verticalAlign:"top"}} align="left">
                      <OwcButton elevated style={{ width: "fit-content" }}
                          onclick={() => this.handleCancelClick()}>
                        Clear Unsaved Changes
                      </OwcButton>
                    </td>

                    <td style={{verticalAlign:"top"}} align="right">
                      <OwcButton style={{ width: "fit-content" }}
                          disabled={((submissionState === null)
                                      || (submissionState === "Saving ...")
                                      || (submissionState.includes("Successfully"))) ? true : false}
                          onclick={() => this.handleSubmitClick()}>
                        {submissionState === "Saving ..." ? submissionState : "Save Changes"}
                      </OwcButton>
                    </td>
                  </tr>
                </tbody>
              </table>
              <OwcTypography variant="title6" style={{ marginBottom: 8, textAlign: "right", color: messageColour }}>
                {submissionState === "Saving ..." ? "" : submissionState}
              </OwcTypography>
            </div>
          </div>
        );
      }
    }
  }
}

export default ImplementationTrackingUseCase;
